import { useState } from "react";
import axios from "axios";
import { Check, Heart, Mail, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  "Tutte le pratiche video gratuite",
  "Mistik See e Kalunet — Viaggi P.O.T.",
  "Newsletter mensile con nuovi contenuti",
  "Accesso anticipato a eventi e corsi",
];

const FONTI = [
  "Social media",
  "Passaparola",
  "Google",
  "Evento o corso",
  "Altro",
];

export default function Community() {
  const [form, setForm] = useState({ nome: "", email: "", fonte: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim() || !form.fonte) {
      toast.error("Compila tutti i campi per continuare");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/community/signup`, {
        nome: form.nome.trim(),
        email: form.email.trim(),
        fonte: form.fonte,
      });
      setSuccess(true);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Si è verificato un errore. Riprova.";
      toast.error(typeof msg === "string" ? msg : "Errore");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="px-6 pt-16 pb-10" data-testid="community-thankyou">
        <div className="bg-[#f9f5ef] rounded-[24px] p-8 flex flex-col items-center text-center shadow-[0_20px_50px_-25px_rgba(46,40,32,0.3)]"
             style={{ borderTop: "2px solid #941a7c" }}>
          <div className="w-16 h-16 rounded-full bg-[#941a7c]/10 flex items-center justify-center mb-5">
            <Heart strokeWidth={1.5} className="w-8 h-8 text-[#941a7c]" />
          </div>
          <h2 className="font-serif-display text-3xl text-[#2e2820] leading-tight">
            Benvenuto nella <span className="italic">Community</span>
          </h2>
          <p className="font-body text-sm text-[#7a6f65] mt-3 leading-relaxed">
            Grazie per esserti unito a noi. Controlla la tua email per i prossimi passi nel tuo cammino.
          </p>
          <div className="mt-6 flex items-center gap-2 text-[#7a6f65] font-body text-xs tracking-wide">
            <Mail strokeWidth={1.5} className="w-4 h-4" />
            <span>{form.email}</span>
          </div>
          <button
            data-testid="community-back-btn"
            onClick={() => { setSuccess(false); setForm({ nome: "", email: "", fonte: "" }); }}
            className="mt-8 font-body text-sm tracking-[0.2em] uppercase text-[#e07818] hover:text-[#2e2820] transition-colors"
          >
            Torna al modulo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 pt-10" data-testid="community-screen">
      <header className="mb-6">
        <p className="font-body text-[11px] tracking-[0.4em] uppercase text-[#941a7c]">
          Community LSW
        </p>
        <h1 className="font-serif-display text-[36px] leading-[1.05] tracking-tight text-[#2e2820] mt-2">
          Unisciti alla <span className="italic">Community</span>
        </h1>
        <p className="font-body text-base text-[#7a6f65] leading-relaxed mt-3">
          Entra a far parte di un cammino condiviso. Ricevi pratiche, viaggi e ispirazione direttamente nella tua casella.
        </p>
      </header>

      {/* Benefits */}
      <section
        data-testid="community-benefits"
        className="rounded-[18px] p-5 bg-[#2e9020]/10"
        style={{ borderTop: "2px solid #2e9020" }}
      >
        <h2 className="font-serif-display text-xl text-[#2e2820] mb-3">Cosa riceverai</h2>
        <ul className="flex flex-col gap-2.5">
          {BENEFITS.map((b) => (
            <li key={b} className="flex items-start gap-3 font-body text-sm text-[#2e2820]/85">
              <span className="mt-0.5 w-5 h-5 rounded-full bg-[#2e9020] text-white flex items-center justify-center shrink-0">
                <Check strokeWidth={2.5} className="w-3 h-3" />
              </span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="mt-6 flex flex-col gap-3"
        data-testid="community-form"
      >
        <div>
          <label className="font-body text-xs tracking-[0.2em] uppercase text-[#7a6f65] ml-1">Nome</label>
          <input
            data-testid="community-input-nome"
            type="text"
            value={form.nome}
            onChange={handleChange("nome")}
            placeholder="Il tuo nome"
            className="mt-1.5 w-full bg-[#f9f5ef] border border-[#ede7df] rounded-[18px] px-4 py-4 text-[#2e2820] font-body placeholder:text-[#2e2820]/35 focus:outline-none focus:ring-2 focus:ring-[#e07818] focus:border-transparent transition-all"
            required
          />
        </div>
        <div>
          <label className="font-body text-xs tracking-[0.2em] uppercase text-[#7a6f65] ml-1">Email</label>
          <input
            data-testid="community-input-email"
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            placeholder="tu@esempio.it"
            className="mt-1.5 w-full bg-[#f9f5ef] border border-[#ede7df] rounded-[18px] px-4 py-4 text-[#2e2820] font-body placeholder:text-[#2e2820]/35 focus:outline-none focus:ring-2 focus:ring-[#e07818] focus:border-transparent transition-all"
            required
          />
        </div>
        <div>
          <label className="font-body text-xs tracking-[0.2em] uppercase text-[#7a6f65] ml-1">Come hai conosciuto LSW?</label>
          <div className="relative mt-1.5">
            <select
              data-testid="community-select-fonte"
              value={form.fonte}
              onChange={handleChange("fonte")}
              className="appearance-none w-full bg-[#f9f5ef] border border-[#ede7df] rounded-[18px] px-4 py-4 pr-10 text-[#2e2820] font-body focus:outline-none focus:ring-2 focus:ring-[#e07818] focus:border-transparent transition-all"
              required
            >
              <option value="" disabled>Scegli un&apos;opzione</option>
              {FONTI.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <ChevronDown strokeWidth={1.5} className="w-5 h-5 absolute right-4 top-1/2 -translate-y-1/2 text-[#2e2820]/50 pointer-events-none" />
          </div>
        </div>

        <button
          data-testid="community-submit-btn"
          type="submit"
          disabled={submitting}
          className="mt-3 w-full bg-[#2e2820] text-[#f9f5ef] rounded-full py-4 font-body text-base tracking-wide transition-all hover:bg-[#1a1612] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting ? "Invio in corso…" : "Unisciti alla Community"}
        </button>
      </form>
    </div>
  );
}
